import type { StaxMood } from "@/components/stax/Stax";
import type { ActionSummary } from "../api/contract";
import type { PublicationState } from "../hooks/usePublication";
import type { LiveStatus } from "./workspaceState";

export type Tone = "neutral" | "progress" | "done" | "attention";

export interface ActionDescription {
  title: string;
  detail: string;
  tone: Tone;
}

const SUBJECTS: Record<string, string> = {
  publish: "Publish",
  rollback: "Put an earlier version live",
  unpublish: "Take your site offline",
  "content.patch": "Edit your content",
  "content.edit": "Change text from your comments",
  "content.patch-instance": "Change text here",
  "bundle.replace": "New design",
  "design.restore": "Restore an earlier design",
  "draft.restore": "Restore an earlier version",
  "bundle.bootstrap": "Build your first draft",
  "brand.set": "Update your brand guidelines",
  "brand.restore": "Restore an earlier brand",
  "site.set": "Update your site details",
  "content.add": "Add content",
  "content.delete": "Delete content",
  "content.restore": "Restore content",
  "collection.reorder": "Reorder your list",
  "section.hide": "Hid a section",
  "media.replace": "Replaced a photo",
  "media.crop": "Cropped a photo",
  "media.bind": "Replaced a photo",
  "change.undo": "Undo a change",
};

const SUCCEEDED: Record<string, string> = {
  publish: "Published",
  rollback: "Earlier version put live",
  "design.restore": "Design restored",
  "brand.restore": "Brand restored",
  unpublish: "Taken offline",
  "section.hide": "Saved",
  "media.replace": "Saved",
  "media.crop": "Saved",
  "media.bind": "Saved",
};

function subject(kind: string): string {
  return SUBJECTS[kind] ?? "Change to your site";
}

/** Plain-language line for the activity list. Never exposes internal errors or identifiers. */
export function describeAction(action: ActionSummary): ActionDescription {
  const title = subject(action.kind);
  const publication = action.review !== null;
  switch (action.state) {
    case "queued":
      return { title, detail: "Waiting to start", tone: "progress" };
    case "running":
      return { title, detail: publication ? "Building your pages" : "Working on it", tone: "progress" };
    case "awaiting-confirmation":
      return { title, detail: "Ready for your review", tone: "attention" };
    case "succeeded":
      // Historical outcome only; the header carries what is serving now.
      return { title, detail: SUCCEEDED[action.kind] ?? "Saved", tone: "done" };
    case "conflict":
      return { title, detail: publication ? "Replaced by a newer publish" : "Skipped because your site changed first", tone: "neutral" };
    case "failed":
      return { title, detail: publication ? "Didn’t finish. This didn’t publish a change." : "Didn’t finish. Nothing was changed.", tone: "attention" };
    case "cancelled":
      return { title, detail: "Cancelled", tone: "neutral" };
  }
}

/** Where a design review stands, for the bubble. */
export type DesignSignal = "ready" | "reviewing" | "applying" | null;

/** Label and signal for the collapsed admin bubble. Each phase says what is actually happening. */
export function bubbleLabel(state: PublicationState, reviewWaiting: boolean, design: DesignSignal = null): { label: string; tone: Tone | null } {
  switch (state.phase) {
    case "idle":
      if (design === "applying") return { label: "Applying design", tone: "progress" };
      if (design === "reviewing") return { label: "Design review", tone: "attention" };
      if (reviewWaiting) return { label: "Review waiting", tone: "attention" };
      if (design === "ready") return { label: "Design ready", tone: "attention" };
      return { label: "Ask Stax", tone: null };
    case "preparing":
      return { label: "Preparing review", tone: "progress" };
    case "reviewing":
    case "blocked":
      return { label: "Review", tone: "attention" };
    case "confirming":
    case "publishing":
      return { label: "Publishing", tone: "progress" };
    case "prepare-unknown":
    case "confirm-unknown":
      return { label: "Check publish", tone: "attention" };
    case "settled":
      return state.action.state === "succeeded"
        ? { label: "Published", tone: "done" }
        : { label: "Publish result", tone: "attention" };
  }
}

/** How Stax acts on the bubble. Mirrors `bubbleLabel`, so the pose never says more than the label. */
export function bubbleMood(state: PublicationState, tone: Tone | null): StaxMood {
  if (state.phase === "prepare-unknown" || state.phase === "confirm-unknown") return "problem";
  if (state.phase === "settled" && state.action.state !== "succeeded") return "problem";
  switch (tone) {
    case "progress":
      return "working";
    case "attention":
      return "attention";
    case "done":
      return "done";
    default:
      return "idle";
  }
}

/**
 * How Stax acts wherever it appears (the bubble and the chat header): the bubble's mood, except that
 * Stax is working while a reply is being written and shows a problem when the chat can't be reached.
 */
export function staxMood(state: PublicationState, tone: Tone | null, chat: { replying: boolean; failed: boolean }): StaxMood {
  const mood = bubbleMood(state, tone);
  if (mood === "problem" || chat.failed) return "problem";
  if (chat.replying) return "working";
  return mood;
}

export function describeLiveStatus(status: LiveStatus): { label: string; tone: Tone } {
  switch (status.kind) {
    case "never-published":
      return { label: "Not published yet", tone: "neutral" };
    case "matches-draft":
      return { label: "Live and up to date", tone: "done" };
    case "has-changes":
      return { label: "Changes not published", tone: "attention" };
    case "live":
      return { label: "Live", tone: "done" };
  }
}
